import { useEffect, useState } from 'react';
import { GenericAbortSignal } from 'axios';

import { useInfiniteQuery } from '@tanstack/react-query';

import DischargeReason, { DischargeReasonOptions } from 'models/DischargeReason';
import { Paginated } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const dischargeReasonsQueryKeys = {
  index: ['dischargeReasons'] as const,
  planningDischargeOptions: ['planningDischargeOptions'] as const,
};

type IndexParams = BaseIndexQueryParams & {
  groupType?: string;
};

export async function indexDischargeReasons(params: IndexParams, signal: GenericAbortSignal) {
  return http.get<Paginated<DischargeReasonOptions>>(`discharge_reasons`, { params, signal }).then((res) => ({
    ...res.data,
    data: res.data.data.map((x) => new DischargeReason(x)),
  }));
}

export const useInfiniteDischargeReasons = (params: IndexParams) => {
  const [search, setSearch] = useState<string>();

  const options = useInfiniteQuery({
    queryKey: [dischargeReasonsQueryKeys.index, { ...params, search }],
    queryFn: ({ signal, pageParam }) =>
      indexDischargeReasons({ pageSize: 10, ...params, search, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: false,
  });

  useEffect(() => {
    if (search !== undefined) options.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { options, onDebouncedSearch: setSearch };
};

export type PlanningDischargeOption = {
  id: string;
  name: string;
};

const selectPlanningDischargeOptions = (res: Paginated<DischargeReason>): PlanningDischargeOption[] => {
  const uniqueGroupTypes = new Set<string>();
  res.data.forEach((reason) => {
    if (reason.dischargedToGroupType?.displayName) {
      uniqueGroupTypes.add(reason.dischargedToGroupType.displayName);
    }
  });

  const options = Array.from(uniqueGroupTypes);
  options.push('Home');
  options.sort((a, b) => a.localeCompare(b));
  return options.map((name) => ({ id: name, name: name }));
};

type PlanningDischargeOptionsParams = {
  groupType?: string;
  enabled?: boolean;
  [key: string]: any;
};

export const usePlanningDischargeOptions = (params?: PlanningDischargeOptionsParams) => {
  const { enabled = true, ...queryParams } = params ?? {};

  const options = useInfiniteQuery({
    queryKey: [dischargeReasonsQueryKeys.planningDischargeOptions, queryParams],
    queryFn: ({ signal }) =>
      indexDischargeReasons({ include: 'dischargedToGroupType', pageSize: 250, ...queryParams }, signal),
    enabled,
    initialPageParam: 1,
    getNextPageParam: () => undefined, // Single page, no pagination needed
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        data: selectPlanningDischargeOptions(page),
      })),
    }),
  });

  return { options, onDebouncedSearch: () => {} };
};
